import React from 'react'

import { flatten } from 'ramda'

import { PagedResponse, SerializableFilterGroup } from 'api/AccountActivityAPI/types'
import { DateRange } from 'components/DateRangePicker'
import { transformFiltersToBackend } from 'utils/deserializeFilters'
import { FilterableEntity, FilterOperator, FilterPage, FilterValueType } from 'utils/filters'

import {
  AccountOpportunityFilter,
  AccountOpportunityFilterType
} from './../pages/ActivityDashboard/AccountOpportunityContext'
import { useAppSelector } from './useRedux'

const CURRENT_USER_MARKER = '<userId>'

export const getCurrentState = <T>(
  pagedData: PagedResponse<T>[] | undefined,
  error: any,
  size: number | undefined
) => {
  const data = flatten(pagedData?.map(pagedData => pagedData.data) ?? [])
  const isLoadingInitialData = !pagedData && !error
  const hasLoadedInitialData = !!(pagedData && !error)
  const isLoadingMore = !!(
    isLoadingInitialData ||
    (pagedData && typeof pagedData[(size ?? 1) - 1] === 'undefined')
  )
  const isEmpty = pagedData?.[0]?.totalCount === 0
  const isReachingEnd = !!(isEmpty || (pagedData && !pagedData[pagedData.length - 1].nextPagePath))

  return {
    data,
    isLoadingInitialData,
    hasLoadedInitialData,
    isLoadingMore,
    isReachingEnd
  }
}

export const setResultSetSize = <T>(
  setResultSize: (value: React.SetStateAction<number | undefined>) => void,
  pagedData: PagedResponse<T>[] | undefined,
  resultSize: number | undefined
) => {
  if (pagedData?.[0]?.totalCount !== undefined && pagedData?.[0]?.totalCount !== resultSize)
    setResultSize(pagedData[0].totalCount)
}

const substituteCurrentUser = (
  filterGroup: SerializableFilterGroup,
  currentUserId: number | undefined
): SerializableFilterGroup => {
  const substitute = (value: string): string =>
    value === CURRENT_USER_MARKER ? currentUserId?.toString() || '0' : value

  const updatedAccountOwnerFilter = filterGroup.accountOwnerFilter && {
    ...filterGroup.accountOwnerFilter,
    values: filterGroup.accountOwnerFilter?.values?.map(substitute)
  }

  const updatedAttributesFilters = filterGroup.attributeFilters?.map(attributeFilter => {
    return {
      ...attributeFilter,
      values: Array.isArray(attributeFilter.values)
        ? attributeFilter.values?.map(substitute)
        : attributeFilter.values
    }
  })

  return {
    ...filterGroup,
    accountOwnerFilter: updatedAccountOwnerFilter,
    attributeFilters: updatedAttributesFilters
  }
}

export const useTransformedFilters = (
  filterPage: FilterPage,
  currentUserId: number | undefined,
  customDateRange?: DateRange,
  opportunities?: AccountOpportunityFilterType
): {
  searchQuery: string | undefined
  dateRange: DateRange | undefined
  serializableFilterGroups: SerializableFilterGroup[]
} => {
  const { searchQuery, dateRange, filterGroupsByPage, loaded } = useAppSelector(
    ({ filters }) => filters
  )
  //eslint-disable-next-line
  let filterGroups = (loaded && [...filterGroupsByPage[filterPage]]) || [[]]

  /**
   * This is to support SFDC opportunity filters. We need to add a filter to the filterGroup[0] array
   * in order to apply it, but not show in the UI
   */
  if (filterGroups && opportunities && opportunities !== AccountOpportunityFilter.ALL && loaded) {
    filterGroups[0] = [
      ...(filterGroups[0] ?? []),
      {
        filterType: FilterableEntity.CUSTOM_ATTRIBUTE,
        filterSubtype: 'sfdc_has_open_opportunities',
        attributeType: 'sfdc_has_open_opportunities',
        operator:
          opportunities === AccountOpportunityFilter.NONE
            ? FilterOperator.DOES_NOT_EQUAL
            : FilterOperator.EQUALS,
        valueType: FilterValueType.BOOLEAN,
        values: ['true']
      }
    ]
  }

  const serializableFilterGroups = transformFiltersToBackend(filterGroups).map(filterGroup =>
    substituteCurrentUser(filterGroup, currentUserId)
  )

  return {
    serializableFilterGroups,
    searchQuery: searchQuery,
    dateRange: customDateRange || dateRange
  }
}

export interface PagedResponseData<T> {
  data: T[]
  resultSize?: number
  error: any
  mutate: any
  size?: number
  setSize: any
  isLoadingMore: boolean
  isLoadingInitialData: boolean
  hasLoadedInitialData: boolean
  isReachingEnd: boolean
  countData?: number
}
